
import { ActivityCodeApi } from "@/api/ActivityCodeApi";
import { ClientApi } from "@/api/ClientApi";
import { CurrencyApi } from "@/api/CurrencyApi";
import { FormConfigType } from "@/types/dynamicForm";
import { CUSTOM_ELEMENT, HIDDEN_INPUT_TYPE, PHONE_INPUT_TYPE, SELECT_INPUT_TYPE, VALIDATION_ERROR_MESSAGE, Yup } from "@mavichpe/dynamic-forms";
import { ACTIVITY_CODE_LENGTH, TIPOS_DE_IDENTIFICACION } from "@utils/Constants";
import { generateCurrencyOptionLabel } from "@utils/Currency";
import { withIdentificationFormat, withPhoneFormat } from "@utils/Forms";
import { CurrencyIdPubSub, OpenAddClientPubSub, OpenSearchClientByIdentificationPubSub } from "@utils/pubsubs/PubSub";
import { Button } from "reactstrap";

const QuoteDataFormConfig: FormConfigType = {
    sections: [
        {
            "fields": [
                [
                    {
                        "type": "text",
                        "name": "numero",
                        "label": "Numero Cotizacion",
                        "disabled": true,
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "currencyId",
                        "label": "Moneda",
                        "optionsFunction": () => CurrencyApi.optionsList({}, (i) => generateCurrencyOptionLabel(i)),
                        "onChangePubSub": (value: string) => CurrencyIdPubSub.publish({ currencyId: value }),
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 3
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": "text",
                        "name": "reference_number",
                        "label": "Numero Referencia",
                        "visibilityHiddenIf": () => true, // by default this input is hidden, suscrption data make it visible
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "activitycode",
                        // Sin defaultValue: cualquier valor aquí se siembra en el form aunque sea 0
                        // (el select no lo muestra) y Yup lo da por válido al castearlo a "0".
                        "validation": Yup.string().required()
                            .length(ACTIVITY_CODE_LENGTH, "Se permite solo 6 caracteres para este codigo"),
                        "label": "Actividad Economica",
                        "optionsFunction": () => ActivityCodeApi.optionsList({}, undefined, "code"),
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 4,
                                "offset": 1,
                            },
                        },
                    },
                ],
                [
                    {
                        "type": "hidden",
                        "name": "exchange_rate"
                    },
                ]
            ]
        },
        {
            title: "Datos de Cliente",
            "fields": [
                [
                    {
                        "type": "text",
                        "name": "client_code",
                        "exludeFromSubmit": true,
                        "label": "Cod",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 2
                            }
                        }
                    },
                    {
                        "type": SELECT_INPUT_TYPE,
                        "name": "clientId",
                        "label": "Cliente",
                        "optionsFunction": () => ClientApi.optionsList(),
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 9
                        },
                        "validation": Yup.string().required(VALIDATION_ERROR_MESSAGE.REQUIRED_FIELD_ERROR),
                    },
                    {
                        "type": CUSTOM_ELEMENT,
                        "label": " ",
                        "labelSizing": {
                            "sm": 2
                        },
                        "sizing": {
                            "sm": 1
                        },
                        "element": <Button
                            className="mt-2"
                            block
                            color="primary"
                            type="button"
                            onClick={() => OpenAddClientPubSub.publish({ openOffCanvas: true })}>
                            Crear
                        </Button>
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "nombre_factura",
                        "label": "Nombre Facturacion",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 12
                            }
                        }
                    },
                ],
                [
                    {
                        // La razon social se guarda aparte del nombre de facturacion porque
                        // Hacienda espera el nombre legal en Receptor/Nombre y el comercial
                        // en Receptor/NombreComercial. No se edita: sale de la ficha del cliente.
                        "type": HIDDEN_INPUT_TYPE,
                        "name": "razon_social",
                    },
                ],
                [
                    {
                        "type": PHONE_INPUT_TYPE,
                        "name": "tel1",
                        "label": "Telefono 1",
                        "validation": withPhoneFormat(Yup.string().notRequired()),
                    },
                    {
                        "type": PHONE_INPUT_TYPE,
                        "name": "tel2",
                        "label": "Telefono 2",
                        "validation": withPhoneFormat(Yup.string().notRequired()),
                    },
                    {
                        "type": "email",
                        "name": "email",
                        "label": "Email"
                    },
                ],
                [
                    {
                        // La cotizacion no se transmite a Hacienda, asi que no necesita
                        // receptor identificado: la identificacion solo es obligatoria al
                        // facturar, donde la exige `identificationRequiredForEinvoice`.
                        "type": SELECT_INPUT_TYPE,
                        "name": "tipo_identificacion",
                        "label": "Tipo Identificacion",
                        "options": TIPOS_DE_IDENTIFICACION,
                        "sizing": {
                            "sm": 4
                        },
                        "validation": Yup.string().notRequired(),
                    },
                    {
                        "type": "text",
                        "name": "identificacion",
                        "label": "Numero Identificacion",
                        "sizing": {
                            "sm": 4
                        },
                        // Opcional, pero si se captura tiene que venir bien formada: el
                        // dato se arrastra tal cual a la factura que nazca de aqui.
                        "validation": withIdentificationFormat(Yup.string().notRequired()),
                        "suffixButton": true,
                        "suffix": <Button
                            className="rounded-0"
                            block
                            color="primary"
                            type="button"
                            onClick={() => OpenSearchClientByIdentificationPubSub.publish({ openOffCanvas: true })}>
                            Buscar
                        </Button>
                    },
                    {
                        "type": "text",
                        "name": "clientActivitycode",
                        "label": "Codigo Actividad Economica",
                        "maxlength": 6,
                        "sizing": {
                            "sm": 4
                        }
                    },
                ],
                [
                    {
                        "type": "text",
                        "name": "direccion",
                        "label": "Dirección",
                        "labelSizing": {
                            "sm": {
                                "size": 2
                            }
                        },
                        "sizing": {
                            "sm": {
                                "size": 12
                            }
                        }
                    },
                ],
            ]
        }
    ],
};


export default QuoteDataFormConfig;